import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react";
import { AuthContext } from "./auth";
import { fetchMyDeveloperAccount, fetchMyGroups } from "../shared/services";
import type { Group, Organization } from "../shared/types";
import {
  ActiveAccount,
  clearActiveAccount,
  clearLastAccount,
  readActiveAccount,
  readLastAccount,
  writeActiveAccount,
} from "../shared/activeAccount";

/**
 * Where the list of accounts the user may act as has got to.
 *
 * `isHydrated` answers a different question — "has the synchronous storage
 * read happened?" — and consumers kept conflating the two, because a stored
 * team only becomes a real `Group` once this fetch lands. The two failure
 * modes it separates matter: "loaded, and that team is not in the list" means
 * the membership is genuinely gone and the choice must be dropped, while
 * "error" means we simply do not know and must change nothing.
 */
export type AccountsStatus = "loading" | "loaded" | "error";

type ActiveAccountContextType = {
  activeAccount: ActiveAccount | undefined;
  activeTeam: Group | undefined;
  activeGroupId: string | undefined;
  isTeamMode: boolean;
  /** Acting as the user's developer account. */
  isDeveloperMode: boolean;
  /** The user's developer account, once loaded. `undefined` when they have
   * none or it has not loaded yet — `developerAccountStatus` says which. */
  developerAccount: Organization | undefined;
  developerAccountStatus: AccountsStatus;
  accounts: Group[];
  accountsStatus: AccountsStatus;
  /** Shorthand for `accountsStatus === "loaded"`. */
  accountsLoaded: boolean;
  isHydrated: boolean;
  lastAccount: ActiveAccount | undefined;
  selectAccount: (account: ActiveAccount) => void;
  clearAccount: () => void;
  reloadAccounts: () => void;
};

const ActiveAccountContext = createContext<ActiveAccountContextType | undefined>(
  undefined,
);

export const useActiveAccount = (): ActiveAccountContextType => {
  const context = useContext(ActiveAccountContext);
  if (!context) {
    throw new Error("useActiveAccount must be used within an ActiveAccountProvider");
  }
  return context;
};

export const ActiveAccountProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useContext(AuthContext);
  const userId = user?.userId;

  const [activeAccount, setActiveAccount] = useState<ActiveAccount | undefined>();
  const [lastAccount, setLastAccount] = useState<ActiveAccount | undefined>();
  const [accounts, setAccounts] = useState<Group[]>([]);
  const [accountsStatus, setAccountsStatus] = useState<AccountsStatus>("loading");
  const [developerAccount, setDeveloperAccount] = useState<Organization | undefined>();
  const [developerAccountStatus, setDeveloperAccountStatus] =
    useState<AccountsStatus>("loading");
  const [isHydrated, setIsHydrated] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  // This provider outlives sign-out, so a stale choice would otherwise carry
  // into the next user on a shared browser. Mirrors mobile's reset.
  useEffect(() => {
    setActiveAccount(undefined);
    setAccounts([]);
    setAccountsStatus("loading");
    setDeveloperAccount(undefined);
    setDeveloperAccountStatus("loading");
    if (!userId) {
      setLastAccount(undefined);
      setIsHydrated(false);
      return;
    }
    setActiveAccount(readActiveAccount(userId));
    setLastAccount(readLastAccount(userId));
    setIsHydrated(true);
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    let active = true;
    setAccountsStatus("loading");
    fetchMyGroups(userId, true)
      .then((groups) => {
        if (!active) return;
        setAccounts(groups || []);
        setAccountsStatus("loaded");
      })
      .catch((error) => {
        if (!active) return;
        console.error("Failed to load the accounts you can act as", error);
        setAccounts([]);
        // Deliberately NOT "loaded": an empty list here means "we don't know",
        // and treating it as fact would evict a user from a live team on a
        // transient network blip.
        setAccountsStatus("error");
      });
    return () => {
      active = false;
    };
  }, [userId, reloadToken]);

  // Same "none" vs "unknown" split as the teams above: only a definite
  // answer may drop a stored developer choice.
  useEffect(() => {
    if (!userId) return;
    let active = true;
    setDeveloperAccountStatus("loading");
    fetchMyDeveloperAccount()
      .then((organization) => {
        if (!active) return;
        setDeveloperAccount(organization ?? undefined);
        setDeveloperAccountStatus("loaded");
      })
      .catch((error) => {
        if (!active) return;
        console.error("Failed to load your developer account", error);
        setDeveloperAccount(undefined);
        setDeveloperAccountStatus("error");
      });
    return () => {
      active = false;
    };
  }, [userId, reloadToken]);

  // The developer status flips to "loading" in the same update as the reload
  // request, not later in the fetch effect: a caller that just created the
  // account and selected it would otherwise be judged against the previous
  // "none" answer and evicted before the reload could say otherwise.
  const reloadAccounts = useCallback(() => {
    setDeveloperAccountStatus("loading");
    setReloadToken((n) => n + 1);
  }, []);

  // Reconcile stored choices against the loaded list. Only ever runs on a
  // genuine "loaded" — never while loading, and never after a failure.
  useEffect(() => {
    if (!userId || !isHydrated) return;

    // Each kind is judged only by its own list, and only once that list has
    // genuinely loaded; anything not yet known stays put.
    const isResolvable = (account: ActiveAccount | undefined): boolean => {
      if (account?.kind === "team") {
        return (
          accountsStatus !== "loaded" ||
          accounts.some((group) => group.id === account.teamId)
        );
      }
      if (account?.kind === "developer") {
        return developerAccountStatus !== "loaded" || Boolean(developerAccount);
      }
      return true;
    };

    if (!isResolvable(activeAccount)) {
      clearActiveAccount(userId);
      setActiveAccount(undefined);
    }
    if (!isResolvable(lastAccount)) {
      clearLastAccount(userId);
      setLastAccount(undefined);
    }
  }, [
    userId,
    isHydrated,
    accountsStatus,
    accounts,
    developerAccountStatus,
    developerAccount,
    activeAccount,
    lastAccount,
  ]);

  const selectAccount = useCallback(
    (account: ActiveAccount) => {
      if (!userId) return;
      writeActiveAccount(userId, account);
      setActiveAccount(account);
      setLastAccount(account);
    },
    [userId],
  );

  // Leaves `lastAccount` in place on purpose — see `clearActiveAccount`.
  const clearAccount = useCallback(() => {
    if (userId) clearActiveAccount(userId);
    setActiveAccount(undefined);
  }, [userId]);

  const activeTeam = useMemo(() => {
    if (activeAccount?.kind !== "team") return undefined;
    return accounts.find((group) => group.id === activeAccount.teamId);
  }, [activeAccount, accounts]);

  const value = useMemo(
    () => ({
      activeAccount,
      activeTeam,
      activeGroupId: activeAccount?.kind === "team" ? activeAccount.teamId : undefined,
      isTeamMode: activeAccount?.kind === "team",
      isDeveloperMode: activeAccount?.kind === "developer",
      developerAccount,
      developerAccountStatus,
      accounts,
      accountsStatus,
      accountsLoaded: accountsStatus === "loaded",
      isHydrated,
      lastAccount,
      selectAccount,
      clearAccount,
      reloadAccounts,
    }),
    [
      activeAccount,
      activeTeam,
      accounts,
      accountsStatus,
      developerAccount,
      developerAccountStatus,
      isHydrated,
      lastAccount,
      selectAccount,
      clearAccount,
      reloadAccounts,
    ],
  );

  return (
    <ActiveAccountContext.Provider value={value}>
      {children}
    </ActiveAccountContext.Provider>
  );
};
